import { ATTACHMENT_LIMITS } from '@innspel/core';
import type { Fangst } from './typer.ts';

/**
 * «Last opp bilde» (upload an image): PNG or JPEG from a file. The image is decoded to a
 * bitmap here — the decoding is what makes EXIF (location, camera, time) gone when the
 * image is re-encoded at composition (K4). The platform strips again (K9).
 *
 * The limits are checked before decoding, with field names, as the core does for the
 * attachment: type against PNG/JPEG and size against the cap. No private fields — the image
 * is not from the page, so DD-39 has nothing to measure.
 */
export async function readImageFile(fil: Blob & { type: string; name?: string }): Promise<Fangst> {
  if (fil.type !== 'image/png' && fil.type !== 'image/jpeg') {
    throw new Error('type: PNG or JPEG only');
  }
  if (fil.size > ATTACHMENT_LIMITS.maxBytes) {
    throw new Error(`bytes: over ${ATTACHMENT_LIMITS.maxBytes}`);
  }
  const bitmap = await dekod(fil);
  return { bilde: bitmap, width: bitmap.width, height: bitmap.height, automasker: [], kilde: 'fil' };
}

async function dekod(fil: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    // `imageOrientation: 'from-image'` retter opp EXIF-rotasjon FØR taggen kastes,
    // så bildet ikke ender liggende når metadataene er borte.
    try {
      return await createImageBitmap(fil, { imageOrientation: 'from-image' });
    } catch {
      /* fall til Image under */
    }
  }
  const url = URL.createObjectURL(fil);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('could not decode the image'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
